import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
} from 'react';
import {
  Box,
  TextField,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  MenuItem,
  Select,
  FormHelperText,
  InputLabel,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  updateBaseNodeField,
  updateBaseNodeOption,
  updateBaseNodeSectorOptions,
  clearBaseNode,
} from '../store/slices/servicePredictorSlice';
import { validationUtils } from '../utils/validation';
import { VALIDATION_MESSAGES } from '../constants/validationConstants';
import { BASE_NODE_OPTIONS, FORM_DEFAULTS } from '../constants/uiConstants';

export interface Carrier {
  frequency: number;
  bandwidth?: number;
}

export interface BaseNodeUpdateData {
  serial?: string;
  cell?: string;
  sector?: string;
  hostname?: string;
  latitude?: number;
  longitude?: number;
  location?: string;
  azimuth?: number;
  downtilt?: number;
  height_agl?: number;
  height_gps?: number;
  market?: string;
  site?: string;
  region?: string;
  carrier?: Carrier[];
  subscribers?: number;
  planning_id?: string;
  part_number?: string;
  sector_options?: Record<string, string>;
}

export interface BaseNodeJsonData {
  operator?: string;
  serial?: string;
  cell?: string;
  sector?: string;
  region?: string;
  hostname?: string;
  subscribers?: number;
  planning_id?: string;
  part_number?: string;
  latitude?: number;
  longitude?: number;
  market?: string;
  site?: string;
  height_agl?: number;
  height_gps?: number;
  azimuth?: number;
  downtilt?: number;
}

interface BaseNodeFormProps {
  operatorKey: string;
  operator: string;
  onUpdateSector?: (sector: string) => void;
  onUpdateFields?: (data: BaseNodeUpdateData) => void;
}

interface BaseNodeFormData {
  serial: string;
  cell: string;
  sector: string;
  hostname: string;
  latitude: string;
  longitude: string;
  azimuth: string;
  tilt: string;
  height_agl: string;
  height_gps: string;
  market_site: string;
  region: string;
  carrier: string;
  subscribers: string;
  planning_id: string;
  part_number: string;
  baseNodeOption: 'all' | 'nearest' | 'use_specific';
}

export type BaseNodeFormRef = {
  getJsonData: () => BaseNodeJsonData;
  isFormValid: () => boolean;
  clearInputs: (data?: {
    heatmap_exist?: boolean;
    results_exist?: boolean;
  }) => void;
  updateSectorOptions: (sectorOptions: Record<string, string>) => void;
};

const BaseNodeForm = forwardRef<BaseNodeFormRef, BaseNodeFormProps>(
  ({ operator, onUpdateSector }, ref) => {
    const dispatch = useAppDispatch();
    const baseNode = useAppSelector((state) => state.servicePredictor.baseNode);

    const {
      control,
      watch,
      setValue,
      formState: { errors },
      reset,
    } = useForm<BaseNodeFormData>({
      defaultValues: {
        serial: baseNode.serial,
        cell: baseNode.cell,
        sector: baseNode.sector,
        hostname: baseNode.hostname,
        latitude: baseNode.latitude,
        longitude: baseNode.longitude,
        azimuth: baseNode.azimuth,
        tilt: baseNode.tilt,
        height_agl: baseNode.height_agl,
        height_gps: baseNode.height_gps,
        market_site: baseNode.market_site,
        region: baseNode.region,
        carrier: baseNode.carrier,
        subscribers: baseNode.subscribers,
        planning_id: baseNode.planning_id,
        part_number: baseNode.part_number,
        baseNodeOption: baseNode.baseNodeOption,
      },
      mode: 'onBlur',
    });

    const baseNodeOption = watch('baseNodeOption');
    const serial = watch('serial');
    const cell = watch('cell');
    const sector = watch('sector');
    const hostname = watch('hostname');
    const latitude = watch('latitude');
    const longitude = watch('longitude');
    const azimuth = watch('azimuth');
    const tilt = watch('tilt');
    const height_agl = watch('height_agl');
    const height_gps = watch('height_gps');
    const market_site = watch('market_site');
    const region = watch('region');
    const carrier = watch('carrier');
    const subscribers = watch('subscribers');
    const planning_id = watch('planning_id');
    const part_number = watch('part_number');

    useEffect(() => {
      reset({
        serial: baseNode.serial,
        cell: baseNode.cell,
        sector: baseNode.sector,
        hostname: baseNode.hostname,
        latitude: baseNode.latitude,
        longitude: baseNode.longitude,
        azimuth: baseNode.azimuth,
        tilt: baseNode.tilt,
        height_agl: baseNode.height_agl,
        height_gps: baseNode.height_gps,
        market_site: baseNode.market_site,
        region: baseNode.region,
        carrier: baseNode.carrier,
        subscribers: baseNode.subscribers,
        planning_id: baseNode.planning_id,
        part_number: baseNode.part_number,
        baseNodeOption: baseNode.baseNodeOption,
      });
    }, [
      baseNode.serial,
      baseNode.cell,
      baseNode.sector,
      baseNode.hostname,
      baseNode.latitude,
      baseNode.longitude,
      baseNode.azimuth,
      baseNode.tilt,
      baseNode.height_agl,
      baseNode.height_gps,
      baseNode.market_site,
      baseNode.region,
      baseNode.carrier,
      baseNode.subscribers,
      baseNode.planning_id,
      baseNode.part_number,
      baseNode.baseNodeOption,
      reset,
    ]);

    useEffect(() => {
      dispatch(updateBaseNodeField({ field: 'serial', value: serial }));
    }, [serial, dispatch]);

    useEffect(() => {
      dispatch(updateBaseNodeField({ field: 'cell', value: cell }));
    }, [cell, dispatch]);

    useEffect(() => {
      dispatch(updateBaseNodeField({ field: 'sector', value: sector }));
    }, [sector, dispatch]);

    useEffect(() => {
      dispatch(updateBaseNodeField({ field: 'hostname', value: hostname }));
    }, [hostname, dispatch]);

    useEffect(() => {
      dispatch(updateBaseNodeField({ field: 'latitude', value: latitude }));
    }, [latitude, dispatch]);

    useEffect(() => {
      dispatch(updateBaseNodeField({ field: 'longitude', value: longitude }));
    }, [longitude, dispatch]);

    useEffect(() => {
      dispatch(updateBaseNodeField({ field: 'azimuth', value: azimuth }));
    }, [azimuth, dispatch]);

    useEffect(() => {
      dispatch(updateBaseNodeField({ field: 'tilt', value: tilt }));
    }, [tilt, dispatch]);

    useEffect(() => {
      dispatch(updateBaseNodeField({ field: 'height_agl', value: height_agl }));
    }, [height_agl, dispatch]);

    useEffect(() => {
      dispatch(updateBaseNodeField({ field: 'height_gps', value: height_gps }));
    }, [height_gps, dispatch]);

    useEffect(() => {
      dispatch(
        updateBaseNodeField({ field: 'market_site', value: market_site })
      );
    }, [market_site, dispatch]);

    useEffect(() => {
      dispatch(updateBaseNodeField({ field: 'region', value: region }));
    }, [region, dispatch]);

    useEffect(() => {
      dispatch(updateBaseNodeField({ field: 'carrier', value: carrier }));
    }, [carrier, dispatch]);

    useEffect(() => {
      dispatch(
        updateBaseNodeField({ field: 'subscribers', value: subscribers })
      );
    }, [subscribers, dispatch]);

    useEffect(() => {
      dispatch(
        updateBaseNodeField({ field: 'planning_id', value: planning_id })
      );
    }, [planning_id, dispatch]);

    useEffect(() => {
      dispatch(
        updateBaseNodeField({ field: 'part_number', value: part_number })
      );
    }, [part_number, dispatch]);

    useEffect(() => {
      dispatch(updateBaseNodeOption(baseNodeOption));
    }, [baseNodeOption, dispatch]);

    const handleBaseNodeOptionChange = useCallback(
      (value: 'all' | 'nearest' | 'use_specific') => {
        setValue('baseNodeOption', value);
        dispatch(updateBaseNodeOption(value));
      },
      [setValue, dispatch]
    );

    const handleSectorChange = useCallback(
      (value: string) => {
        setValue('sector', value);
        dispatch(updateBaseNodeField({ field: 'sector', value }));
        if (onUpdateSector) {
          onUpdateSector(value);
        }
      },
      [setValue, dispatch, onUpdateSector]
    );

    const validateNumber = useCallback((value: string): boolean | string => {
      if (!value || value.trim() === '') return true;
      return (
        validationUtils.isValidNumber(value) ||
        VALIDATION_MESSAGES.INVALID_NUMBER
      );
    }, []);

    const validateLatitude = useCallback((value: string): boolean | string => {
      if (!value || value.trim() === '') return true;
      return (
        validationUtils.isValidNumber(value) ||
        VALIDATION_MESSAGES.INVALID_NUMBER
      );
    }, []);

    const validateLongitude = useCallback((value: string): boolean | string => {
      if (!value || value.trim() === '') return true;
      return (
        validationUtils.isValidNumber(value) ||
        VALIDATION_MESSAGES.INVALID_NUMBER
      );
    }, []);

    const getJsonData = useCallback((): BaseNodeJsonData => {
      const bnObject: BaseNodeJsonData = {};

      if (
        !baseNode.disabled &&
        baseNode.baseNodeOption !== BASE_NODE_OPTIONS.ALL
      ) {
        bnObject.operator = operator;

        if (serial !== '') bnObject.serial = serial;
        if (cell !== '') bnObject.cell = cell;
        if (sector !== '') bnObject.sector = sector;
        if (region !== '') bnObject.region = region;
        if (hostname !== '') bnObject.hostname = hostname;
        if (subscribers !== '') bnObject.subscribers = parseFloat(subscribers);
        if (planning_id !== '') bnObject.planning_id = planning_id;
        if (part_number !== '') bnObject.part_number = part_number;

        if (latitude !== '') bnObject.latitude = parseFloat(latitude);
        if (longitude !== '') bnObject.longitude = parseFloat(longitude);

        if (market_site !== '') {
          const marketSiteArray = market_site.split('/').map((s) => s.trim());
          const market = marketSiteArray[0] || '';
          const site = marketSiteArray[1] || '';
          if (market !== '') bnObject.market = market;
          if (site !== '') bnObject.site = site;
        }

        if (height_agl !== '') bnObject.height_agl = parseFloat(height_agl);
        if (height_gps !== '') bnObject.height_gps = parseFloat(height_gps);

        if (azimuth !== '') {
          const azimuthValue = parseFloat(azimuth);
          if (azimuthValue !== 0) bnObject.azimuth = azimuthValue;
        }
        if (tilt !== '') {
          const downtiltValue = parseFloat(tilt);
          if (downtiltValue !== 0) bnObject.downtilt = downtiltValue;
        }
      }

      return bnObject;
    }, [
      baseNode.disabled,
      baseNode.baseNodeOption,
      operator,
      serial,
      cell,
      sector,
      region,
      hostname,
      subscribers,
      planning_id,
      part_number,
      latitude,
      longitude,
      market_site,
      height_agl,
      height_gps,
      azimuth,
      tilt,
    ]);

    const isFormValid = useCallback((): boolean => {
      if (baseNode.baseNodeOption === BASE_NODE_OPTIONS.ALL) {
        return true;
      }

      const locationValid =
        (!latitude && !longitude) ||
        (validationUtils.isValidNumber(latitude) &&
          validationUtils.isValidNumber(longitude));

      return (
        validationUtils.isValidNumber(height_agl) &&
        validationUtils.isValidNumber(height_gps) &&
        validationUtils.isValidNumber(subscribers) &&
        locationValid
      );
    }, [
      baseNode.baseNodeOption,
      height_agl,
      height_gps,
      subscribers,
      latitude,
      longitude,
    ]);

    // const formatCarrierValues = useCallback(
    //   (key: string, data: any[]): string => {
    //     if (data && data.length > 0) {
    //       const carrier = data
    //         .filter((item) => item[key] !== 0)
    //         .map((item) => item[key])
    //         .join(', ');
    //       return carrier;
    //     }
    //     return '';
    //   },
    //   []
    // );

    useImperativeHandle(
      ref,
      () => ({
        getJsonData,
        isFormValid,
        clearInputs: (data?: {
          heatmap_exist?: boolean;
          results_exist?: boolean;
        }) => {
          reset({
            serial: FORM_DEFAULTS.EMPTY_STRING,
            cell: FORM_DEFAULTS.EMPTY_STRING,
            sector: FORM_DEFAULTS.EMPTY_STRING,
            hostname: FORM_DEFAULTS.EMPTY_STRING,
            latitude: FORM_DEFAULTS.EMPTY_STRING,
            longitude: FORM_DEFAULTS.EMPTY_STRING,
            azimuth: FORM_DEFAULTS.EMPTY_STRING,
            tilt: FORM_DEFAULTS.EMPTY_STRING,
            height_agl: FORM_DEFAULTS.EMPTY_STRING,
            height_gps: FORM_DEFAULTS.EMPTY_STRING,
            market_site: FORM_DEFAULTS.EMPTY_STRING,
            region: FORM_DEFAULTS.EMPTY_STRING,
            carrier: FORM_DEFAULTS.EMPTY_STRING,
            subscribers: FORM_DEFAULTS.EMPTY_STRING,
            planning_id: FORM_DEFAULTS.EMPTY_STRING,
            part_number: FORM_DEFAULTS.EMPTY_STRING,
            baseNodeOption: BASE_NODE_OPTIONS.ALL,
          });
          dispatch(clearBaseNode(data || {}));
        },
        updateSectorOptions: (sectorOptions: Record<string, string>) => {
          dispatch(updateBaseNodeSectorOptions(sectorOptions));
        },
      }),
      [getJsonData, isFormValid, reset, dispatch]
    );

    const renderBaseNodeOptionToggle = useCallback(
      () => (
        <FormControl component="fieldset" sx={{ mb: 2 }}>
          <Controller
            name="baseNodeOption"
            control={control}
            render={({ field }) => (
              <RadioGroup
                row
                value={field.value}
                onChange={(e) => {
                  const value = e.target.value as
                    | 'all'
                    | 'nearest'
                    | 'use_specific';
                  field.onChange(value);
                  handleBaseNodeOptionChange(value);
                }}
                name="baseNodeRadioOptions"
              >
                <FormControlLabel
                  value={BASE_NODE_OPTIONS.ALL}
                  control={<Radio />}
                  label="Find All"
                />
                <FormControlLabel
                  value={BASE_NODE_OPTIONS.NEAREST}
                  control={<Radio />}
                  label="Find Nearest"
                />
                <FormControlLabel
                  value={BASE_NODE_OPTIONS.USE_SPECIFIC}
                  control={<Radio />}
                  label="Use Specific"
                />
              </RadioGroup>
            )}
          />
        </FormControl>
      ),
      [control, handleBaseNodeOptionChange]
    );

    const renderSectorDropdown = useCallback(() => {
      if (Object.keys(baseNode.sectorOptions).length === 0) {
        return null;
      }

      return (
        <Box sx={{ mb: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="sector-select-label">Sector List</InputLabel>
            <Controller
              name="sector"
              control={control}
              render={({ field }) => (
                <>
                  <Select
                    {...field}
                    labelId="sector-select-label"
                    label="Sector List"
                    disabled={baseNode.disabled}
                    onChange={(e) => {
                      field.onChange(e.target.value);
                      handleSectorChange(e.target.value);
                    }}
                  >
                    {Object.entries(baseNode.sectorOptions).map(
                      ([value, label]) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                      )
                    )}
                  </Select>
                  {errors.sector && (
                    <FormHelperText error>
                      {errors.sector.message}
                    </FormHelperText>
                  )}
                </>
              )}
            />
          </FormControl>
        </Box>
      );
    }, [
      baseNode.sectorOptions,
      baseNode.disabled,
      control,
      errors.sector,
      handleSectorChange,
    ]);

    const renderHostnameAndSector = useCallback(
      () => (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Controller
            name="hostname"
            control={control}
            render={({ field }) => (
              <TextField {...field} fullWidth label="Hostname" />
            )}
          />
          <Controller
            name="sector"
            control={control}
            render={({ field }) => (
              <TextField {...field} fullWidth label="Sector" />
            )}
          />
        </Box>
      ),
      [control]
    );

    const renderLatitudeAndLongitude = useCallback(
      () => (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Controller
            name="latitude"
            control={control}
            rules={{
              validate: validateLatitude,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Latitude"
                error={!!errors.latitude}
                helperText={errors.latitude?.message}
              />
            )}
          />
          <Controller
            name="longitude"
            control={control}
            rules={{
              validate: validateLongitude,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Longitude"
                error={!!errors.longitude}
                helperText={errors.longitude?.message}
              />
            )}
          />
        </Box>
      ),
      [
        control,
        errors.latitude,
        errors.longitude,
        validateLatitude,
        validateLongitude,
      ]
    );

    const renderAzimuthAndTilt = useCallback(
      () => (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Controller
            name="azimuth"
            control={control}
            rules={{
              validate: validateNumber,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Azimuth (°)"
                error={!!errors.azimuth}
                helperText={errors.azimuth?.message}
              />
            )}
          />
          <Controller
            name="tilt"
            control={control}
            rules={{
              validate: validateNumber,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Tilt (°)"
                error={!!errors.tilt}
                helperText={errors.tilt?.message}
              />
            )}
          />
        </Box>
      ),
      [control, errors.azimuth, errors.tilt, validateNumber]
    );

    const renderCellAndHeightAgl = useCallback(
      () => (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Controller
            name="cell"
            control={control}
            render={({ field }) => (
              <TextField {...field} fullWidth label="Cell" />
            )}
          />
          <Controller
            name="height_agl"
            control={control}
            rules={{
              validate: validateNumber,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Height AGL (m)"
                error={!!errors.height_agl}
                helperText={errors.height_agl?.message}
              />
            )}
          />
        </Box>
      ),
      [control, errors.height_agl, validateNumber]
    );

    const renderMarketSiteAndHeightGps = useCallback(
      () => (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Controller
            name="market_site"
            control={control}
            render={({ field }) => (
              <TextField {...field} fullWidth label="Market / Site" />
            )}
          />
          <Controller
            name="height_gps"
            control={control}
            rules={{
              validate: validateNumber,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Height MSL (m)"
                error={!!errors.height_gps}
                helperText={errors.height_gps?.message}
              />
            )}
          />
        </Box>
      ),
      [control, errors.height_gps, validateNumber]
    );

    const renderRegionAndCarrier = useCallback(
      () => (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Controller
            name="region"
            control={control}
            render={({ field }) => (
              <TextField {...field} fullWidth label="Region" />
            )}
          />
          <Controller
            name="carrier"
            control={control}
            render={({ field }) => (
              <TextField {...field} fullWidth label="Carriers (MHz)" />
            )}
          />
        </Box>
      ),
      [control]
    );

    const renderSerialAndSubscribers = useCallback(
      () => (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Controller
            name="serial"
            control={control}
            render={({ field }) => (
              <TextField {...field} fullWidth label="Serial Number" />
            )}
          />
          <Controller
            name="subscribers"
            control={control}
            rules={{
              validate: validateNumber,
            }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                label="Subscribers"
                error={!!errors.subscribers}
                helperText={errors.subscribers?.message}
              />
            )}
          />
        </Box>
      ),
      [control, errors.subscribers, validateNumber]
    );

    const renderPartNumberAndPlanningId = useCallback(
      () => (
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Controller
            name="part_number"
            control={control}
            render={({ field }) => (
              <TextField {...field} fullWidth label="Part Number" />
            )}
          />
          <Controller
            name="planning_id"
            control={control}
            render={({ field }) => (
              <TextField {...field} fullWidth label="Planning ID" />
            )}
          />
        </Box>
      ),
      [control]
    );

    return (
      <Box>
        <Typography
          variant="subtitle1"
          sx={{ mb: 2, fontWeight: 'bold', fontSize: '1rem' }}
        >
          Base Node
        </Typography>

        {renderBaseNodeOptionToggle()}
        {baseNodeOption === BASE_NODE_OPTIONS.USE_SPECIFIC && (
          <>
            {renderSectorDropdown()}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {renderHostnameAndSector()}
              {renderLatitudeAndLongitude()}
              {renderAzimuthAndTilt()}
              {renderCellAndHeightAgl()}
              {renderMarketSiteAndHeightGps()}
              {renderRegionAndCarrier()}
              {renderSerialAndSubscribers()}
              {renderPartNumberAndPlanningId()}
            </Box>
          </>
        )}
      </Box>
    );
  }
);

BaseNodeForm.displayName = 'BaseNodeForm';

export const BaseNodeFormWithRef = BaseNodeForm;

export default BaseNodeForm;
